import { useCallback, useEffect, useMemo, useState } from 'react';
import { SandboxFreigabe } from '../types';
import { sandboxService } from '../services/sandboxService';
import { useAuth } from './useAuth';

const CHANGED_EVENT = 'sandbox-freigaben-changed';

// Modulweiter Cache: viele Komponenten (Sidebar, Header, Tabellenzeilen) nutzen den Hook
// gleichzeitig, es soll aber nur ein Request pro Aenderung laufen.
let cacheUserId: number | null = null;
let cache: SandboxFreigabe[] | null = null;
let inflight: Promise<SandboxFreigabe[]> | null = null;

async function fetchFreigaben(userId: number, force = false): Promise<SandboxFreigabe[]> {
  if (cacheUserId !== userId) {
    cache = null;
    cacheUserId = userId;
  }
  if (!force && cache) return cache;
  if (!inflight) {
    inflight = sandboxService.freigaben
      .list()
      .then((rows) => {
        cache = rows;
        return rows;
      })
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

async function refreshAndBroadcast(userId: number) {
  try {
    await fetchFreigaben(userId, true);
  } finally {
    window.dispatchEvent(new Event(CHANGED_EVENT));
  }
}

/**
 * Lese-Freigaben des Hauptaccounts in die isolierte Umgebung.
 *
 * Liefert Status-Abfragen (Audit/Finding freigegeben?) und Toggles, damit die
 * Freigaben ueberall im Hauptaccount sichtbar und steuerbar sind (Sidebar-Badge,
 * Audit-/Finding-Header, Findings-Tabelle, Seite "Isolierte Umgebung").
 */
export function useSandboxFreigaben() {
  const { user } = useAuth();
  const userId = user && user.scope !== 'restricted' ? user.id : null;
  const [freigaben, setFreigaben] = useState<SandboxFreigabe[]>(() => (userId && cacheUserId === userId && cache) || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setFreigaben([]);
      return;
    }
    let active = true;
    setLoading(true);
    fetchFreigaben(userId)
      .then((rows) => active && setFreigaben(rows))
      .catch(() => active && setFreigaben([]))
      .finally(() => active && setLoading(false));

    const handler = () => {
      if (active) setFreigaben(cache ?? []);
    };
    window.addEventListener(CHANGED_EVENT, handler);
    return () => {
      active = false;
      window.removeEventListener(CHANGED_EVENT, handler);
    };
  }, [userId]);

  const reload = useCallback(async () => {
    if (userId) await refreshAndBroadcast(userId);
  }, [userId]);

  const auditIds = useMemo(() => new Set(freigaben.filter((f) => f.typ === 'audit').map((f) => f.audit_id)), [freigaben]);
  const findingIds = useMemo(
    () => new Set(freigaben.filter((f) => f.typ === 'schwachstelle').map((f) => f.schwachstelle_id as number)),
    [freigaben],
  );

  const isAuditShared = useCallback((auditId?: number | null) => !!auditId && auditIds.has(auditId), [auditIds]);
  const isFindingSharedDirectly = useCallback((id?: number | null) => !!id && findingIds.has(id), [findingIds]);
  const isFindingShared = useCallback(
    (id?: number | null, auditId?: number | null) => isFindingSharedDirectly(id) || isAuditShared(auditId),
    [isFindingSharedDirectly, isAuditShared],
  );

  const toggleAudit = useCallback(
    async (auditId: number) => {
      if (!userId) return;
      const existing = (cache ?? freigaben).find((f) => f.typ === 'audit' && f.audit_id === auditId);
      if (existing) await sandboxService.freigaben.delete(existing.id);
      else await sandboxService.freigaben.createForAudit(auditId);
      await refreshAndBroadcast(userId);
    },
    [userId, freigaben],
  );

  const toggleFinding = useCallback(
    async (schwachstelleId: number) => {
      if (!userId) return;
      const existing = (cache ?? freigaben).find((f) => f.typ === 'schwachstelle' && f.schwachstelle_id === schwachstelleId);
      if (existing) await sandboxService.freigaben.delete(existing.id);
      else await sandboxService.freigaben.createForFinding(schwachstelleId);
      await refreshAndBroadcast(userId);
    },
    [userId, freigaben],
  );

  const remove = useCallback(
    async (freigabeId: number) => {
      await sandboxService.freigaben.delete(freigabeId);
      if (userId) await refreshAndBroadcast(userId);
    },
    [userId],
  );

  return {
    freigaben,
    loading,
    count: freigaben.length,
    reload,
    isAuditShared,
    isFindingShared,
    isFindingSharedDirectly,
    toggleAudit,
    toggleFinding,
    remove,
  };
}
